import { useState, useEffect } from "react";
import apiClient from "../../shared/API/apiClient";

const EMPTY_FORM = {
  field_key: "",
  field_label: "",
  field_type: "",
  is_required: 0,
  display_order: "",
  options: [""],
};

const ActivityModal = ({ onClose, onSaved, editData, selectedActivity }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [apiError, setApiError] = useState("");

  //  Prefill
  useEffect(() => {
    if (editData) {
      setForm({
        field_key: editData.field_key || "",
        field_label: editData.field_label || "",
        field_type: editData.field_type || "",
        is_required: editData.is_required || 0,
        display_order: editData.display_order || "",
        options: editData.options?.length
          ? editData.options.map((o) => o.value)
          : [""],
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [editData]);

  // 🔹 Handle Change
  const handleChange = (field) => (e) => {
    const value =
      field === "is_required" ? (e.target.checked ? 1 : 0) : e.target.value;

    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  //  Options Change
  const handleOptionChange = (index, value) => {
    const updated = [...form.options];
    updated[index] = value;

    setForm((prev) => ({ ...prev, options: updated }));
  };

  const addOption = () => {
    setForm((prev) => ({
      ...prev,
      options: [...prev.options, ""],
    }));
  };

  const removeOption = (index) => {
    const updated = form.options.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, options: updated }));
  };

  // 🔹 Validation
  const validate = () => {
    const e = {};

    if (!form.field_key) e.field_key = "Field Key required";
    if (!form.field_label) e.field_label = "Field Label required";
    if (!form.field_type) e.field_type = "Field Type required";
    if (!form.display_order) e.display_order = "Display order required";

    if (form.field_type === "dropdown") {
      const validOptions = form.options.filter(
        (opt) => typeof opt === "string" && opt.trim() !== ""
      );
      if (validOptions.length === 0) {
        e.options = "At least one option required";
      }
    }

    return e;
  };

  //  Submit handle API ADD EDIT
  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) return setErrors(e);

    setSaving(true);
    setApiError("");

    try {
      const payload = {
        field_label: form.field_label,
        field_type: form.field_type,
        is_required: form.is_required,
        display_order: Number(form.display_order),
        options:
          form.field_type === "dropdown"
            ? form.options.filter(
                (opt) => typeof opt === "string" && opt.trim() !== ""
              )
            : [],
      };

      let res;

      if (editData) {
        // UPDATE API
        res = await apiClient.put(
          `/api/activity_type/update/${editData.id}`,
          payload
        );
      } else {
        // ADD API
        res = await apiClient.post("/api/activity_type/store", {
          ...payload,
          activity_type_id: selectedActivity,
          field_key: form.field_key,
        });
      }

      if (res.data.success) {
        onSaved && onSaved(); // refresh + toast parent me
        setForm(EMPTY_FORM); // reset
        onClose();
      } else {
        throw new Error(res.data.message);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Something went wrong";
      setApiError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="modal-header">
          <div className="modal-title">
            {editData ? "Edit Activity Field" : "Add Activity Field"}
          </div>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="modal-body">
          {/* Field Key */}
          <div className="org-form-field full">
            <label>Field Key *</label>
            <input
              className={`org-field-input ${
                errors.field_key ? "input-error" : ""
              }`}
              value={form.field_key}
              onChange={handleChange("field_key")}
              disabled={!!editData}
            />
            {errors.field_key && (
              <span className="field-error">{errors.field_key}</span>
            )}
          </div>

          {/* Field Label */}
          <div className="org-form-field full">
            <label>Field Label *</label>
            <input
              className={`org-field-input ${
                errors.field_label ? "input-error" : ""
              }`}
              value={form.field_label}
              onChange={handleChange("field_label")}
            />
            {errors.field_label && (
              <span className="field-error">{errors.field_label}</span>
            )}
          </div>

          {/* Field Type */}
          <div className="org-form-field full">
            <label>Field Type *</label>
            <select
              className={`org-field-input ${
                errors.field_type ? "input-error" : ""
              }`}
              value={form.field_type}
              onChange={handleChange("field_type")}
            >
              <option value="">Select</option>
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="dropdown">Dropdown</option>
              <option value="date">Date</option>
              <option value="file">File</option>
            </select>

            {errors.field_type && (
              <span className="field-error">{errors.field_type}</span>
            )}
          </div>

          {/* Required */}
          <div className="org-form-field full">
            <label className="org-field-label">Required</label>

            <div className="checkbox-wrapper">
              <input
                type="checkbox"
                className="custom-checkbox"
                checked={form.is_required === 1}
                onChange={handleChange("is_required")}
              />
              <span className="checkbox-text">Mark as required</span>
              {/* {errors.field_label && (
                <span className="field-error">{errors.field_label}</span>
              )} */}
            </div>
          </div>

          {/* Display Order */}
          <div className="org-form-field full">
            <label>Display Order *</label>
            <input
              type="number"
              className={`org-field-input ${
                errors.display_order ? "input-error" : ""
              }`}
              value={form.display_order}
              onChange={handleChange("display_order")}
            />
            {errors.display_order && (
              <span className="field-error">{errors.display_order}</span>
            )}
          </div>

          {/* Dropdown Options */}
          {form.field_type === "dropdown" && (
            <div className="org-form-field full">
              <label className="org-field-label">Options *</label>

              {form.options.map((opt, i) => (
                <div key={i} className="option-row">
                  <input
                    className="org-field-input option-input"
                    placeholder={`Option ${i + 1}`}
                    value={opt}
                    onChange={(e) => handleOptionChange(i, e.target.value)}
                  />

                  <button
                    type="button"
                    className="option-remove-btn"
                    onClick={() => removeOption(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="org-btn-save option-add-btn"
                onClick={addOption}
              >
                + Add Option
              </button>

              {errors.options && (
                <span className="field-error">{errors.options}</span>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          {apiError && <span className="org-save-error">✕ {apiError}</span>}
          <button className="org-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="org-btn-save" onClick={handleSubmit}>
            {saving ? "Saving..." : editData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityModal;
